import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { useTheme } from '@mui/material'
import type { Theme } from '@mui/system'

import { Icon } from '@mxenabled/mxui'

import {
  Drawer,
  useAppConfig,
  useCategoryStore,
  useGlobalCopyStore,
  useGlobalUiStore,
  useTransactionStore,
} from '../../common'
import { CategoryGuids } from '../../core/constants'
import { Transaction } from '../../core'
import { exportTransactionsToCSV } from '../../core/utils/TransactionUtils'

import { TransactionDetails, TransactionList } from '../../transactions'
import { getIncomeData, getSpendingData } from '../utils/SpendingData'
import SpendingChart from './SpendingChart'
import SpendingTabLabel from './SpendingTabLabel'

const getIncomeColors = (theme: Theme) => {
  return [
    theme.palette.chartMono.chartMono5,
    theme.palette.chartMono.chartMono4,
    theme.palette.chartMono.chartMono3,
    theme.palette.chartMono.chartMono2,
    theme.palette.chartMono.chartMono1,
    theme.palette.chartMono.chartMono6,
  ]
}

const Spending = () => {
  const theme = useTheme()
  const { is_mobile_webview } = useAppConfig()

  const { clientConfig } = useAppConfig()
  const { categories, dateRangeCategoryTotals } = useCategoryStore()

  const {
    goals: goalsCopy,
    spending: spendingCopy,
    transactions: transactionCopy,
  } = useGlobalCopyStore()
  const { displayedDateRange, selectedAccountGuids } = useGlobalUiStore()
  const {
    setFilter,
    sortedTransactions: transactions,
    sortedTransactionsWithSplits,
    tags,
  } = useTransactionStore()

  const accountGuids = clientConfig?.master?.deep_link_params?.account_guids
  const view = clientConfig?.master?.deep_link_params?.view

  const [selectedId, setSelectedId] = React.useState('')
  const [selectedTab, setSelectedTab] = React.useState(view === 'income' ? 1 : 0)
  const [transactionGuid, setTransactionGuid] = React.useState('')
  const [transactionsOpen, setTransactionsOpen] = React.useState(false)

  const spendingData = React.useMemo(
    () => getSpendingData(categories, goalsCopy, theme, dateRangeCategoryTotals),
    [categories, dateRangeCategoryTotals],
  )

  const incomeData = React.useMemo(
    () => getIncomeData(categories, dateRangeCategoryTotals, getIncomeColors(theme)),
    [categories, dateRangeCategoryTotals],
  )

  const transaction = React.useMemo(
    () => transactions.find((t) => t.guid === transactionGuid),
    [transactionGuid, transactions],
  )

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    setSelectedId('')
  }

  const handleSelected = (id: string, isSubcategory: boolean = false) => {
    if (isSubcategory) {
      handleSelectedSubcategory(id)
    } else {
      setSelectedId(selectedId === id || id === '0' ? '' : id)
    }
  }

  const handleSelectedSubcategory = (id: string) => {
    const transactionFilter = {
      accounts: accountGuids ? accountGuids : selectedAccountGuids,
      dateRange: displayedDateRange,
    }

    setFilter({
      ...transactionFilter,
      custom: (t: Transaction) =>
        selectedId === 'other' ? t.top_level_category_guid === id : t.category_guid === id,
      showSplits: true,
    })

    setTransactionsOpen(true)
  }

  const handleViewTransactions = (id: string) => {
    const transactionFilter = {
      accounts: accountGuids ? accountGuids : selectedAccountGuids,
      dateRange: displayedDateRange,
    }

    if (selectedTab === 0) {
      // Spending
      if (id === 'other') {
        const other = spendingData.categoryData.find((c) => c.guid === 'other')
        setFilter({
          ...transactionFilter,
          custom: (t: Transaction) =>
            Boolean(other?.categoryTotals?.find((ct) => ct.guid === t.top_level_category_guid)),
        })
      } else {
        setFilter({
          ...transactionFilter,
          custom: (t: Transaction) =>
            id
              ? t.top_level_category_guid === id
              : t.top_level_category_guid !== CategoryGuids.INCOME &&
                t.category_guid !== CategoryGuids.INCOME &&
                t.top_level_category_guid !== CategoryGuids.INVESTMENTS &&
                t.top_level_category_guid !== CategoryGuids.TRANSFER,
          showSplits: Boolean(id),
        })
      }
    } else {
      // Income
      setFilter({
        ...transactionFilter,
        custom: (t: Transaction) =>
          id
            ? t.category_guid === id
            : t.category_guid === CategoryGuids.INCOME ||
              t.top_level_category_guid === CategoryGuids.INCOME,
      })
    }

    setTransactionsOpen(true)
  }

  const handleExportToCsv = () => {
    exportTransactionsToCSV(sortedTransactionsWithSplits, tags)
  }

  return (
    <Box>
      <Tabs onChange={handleTabChange} value={selectedTab} variant="fullWidth">
        <Tab
          label={
            <SpendingTabLabel
              amount={spendingData.totalAmount}
              label={spendingCopy.spend_tab_title}
            />
          }
        />
        <Tab
          label={
            <SpendingTabLabel
              amount={incomeData.totalAmount}
              label={spendingCopy.income_tab_title}
            />
          }
        />
      </Tabs>
      {selectedTab === 0 && (
        <SpendingChart
          data={spendingData}
          onSelected={handleSelected}
          onViewTransactions={handleViewTransactions}
          selectedId={selectedId}
          title={spendingCopy.spend_tab_title}
        />
      )}
      {selectedTab === 1 && (
        <SpendingChart
          data={incomeData}
          onSelected={handleSelected}
          onViewTransactions={handleViewTransactions}
          selectedId={selectedId}
          title={spendingCopy.income_tab_title}
        />
      )}
      <Drawer
        ariaLabelClose={spendingCopy.close_drawer_aria}
        isOpen={transactionsOpen}
        onClose={() => setTransactionsOpen(false)}
        title={spendingCopy.transactions_drawer_title}
      >
        <Stack>
          {!is_mobile_webview && transactions.length > 0 && (
            <Stack direction="row" justifyContent="right" mb={-24} mr={8} mt={8} zIndex={1}>
              <Button
                onClick={handleExportToCsv}
                startIcon={<Icon name="ios_share" />}
                variant="text"
              >
                {transactionCopy.export_csv_btn}
              </Button>
            </Stack>
          )}
          <TransactionList onClick={setTransactionGuid} />
        </Stack>
      </Drawer>
      <Drawer
        ariaLabelClose={spendingCopy.close_drawer_aria}
        isOpen={Boolean(transaction)}
        onClose={() => setTransactionGuid('')}
        title={spendingCopy.transaction_details_drawer_title}
      >
        {transaction && <TransactionDetails transaction={transaction} />}
      </Drawer>
    </Box>
  )
}

export default observer(Spending)
